'use client';

/**
 * FE-06/FE-21 (audit 07-frontend.md) — đăng xuất trước đây chỉ xoá token +
 * `vgt.activeBranchId`. Cache TanStack Query (staleTime 30s / gcTime mặc
 * định 5 phút), cache API của service worker tài xế/kỹ thuật (`*-api`,
 * network-first, fallback 24h) và hàng đợi mutation/ảnh trong IndexedDB đều
 * SỐNG QUA lần đăng xuất. Trên máy dùng chung: tài xế B đăng nhập sau A có
 * thể thấy dữ liệu của A từ cache SW, hoặc hàng đợi offline của A bị phát
 * lại bằng token của B.
 *
 * Hook này theo dõi `user.id` từ `useAuth()` và dọn sạch mỗi khi:
 *   - có → null (đăng xuất)
 *   - id đổi (đổi người dùng trên cùng máy, vd đăng nhập lại user khác)
 *
 * Đặt trong `RootLayout` (`router.tsx`, bên trong `AuthProvider`) — chạy
 * cho MỌI nhánh (`/dashboard`, `/m`, `/kt`), không riêng app tài xế, vì
 * `queryClient` dùng chung một instance cho toàn SPA.
 */
import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth-context';

/** Hậu tố cache API của các service worker trong repo (`m-sw.js`, `kt-sw.js`). */
const SW_API_CACHE_SUFFIX = '-api';

async function clearServiceWorkerApiCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((k) => k.endsWith(SW_API_CACHE_SUFFIX))
        .map((k) => caches.delete(k)),
    );
  } catch {
    // Cache API không khả dụng (Safari riêng tư, trình duyệt cũ) — bỏ qua,
    // không phải lỗi chặn đăng xuất.
  }
}

/**
 * Quyết định có cần dọn cache/hàng đợi không, dựa trên id user trước và sau.
 * Hàm THUẦN — test được không cần render React/IndexedDB (xem
 * `__tests__/use-session-cleanup.test.ts`).
 *
 * - `prevId === undefined` (lần chạy đầu, chưa có mốc so sánh) → không dọn.
 * - `prevId === currentId` (không đổi) → không dọn.
 * - `prevId === null` (chưa đăng nhập → có user, tức đăng nhập bình thường)
 *   → không dọn — không có gì của "người trước" để dọn.
 * - Còn lại (có user → null tức đăng xuất, HOẶC id đổi tức đổi người dùng)
 *   → dọn.
 */
export function shouldCleanupSession(
  prevId: string | null | undefined,
  currentId: string | null,
): boolean {
  if (prevId === undefined) return false;
  if (prevId === currentId) return false;
  if (prevId === null) return false;
  return true;
}

async function clearOfflineQueues(): Promise<void> {
  // Import động: hai module IndexedDB này chỉ thuộc app tài xế (`/m`) và
  // dùng `indexedDB` — tải muộn để không kéo vào bundle của `/dashboard`/`/kt`
  // và không lỗi ở môi trường test (node, không có IndexedDB).
  try {
    const [{ clearAllMutations }, { clearAllPhotos }] = await Promise.all([
      import('@/app/m/_lib/mutation-queue'),
      import('@/app/m/_lib/photo-queue'),
    ]);
    await Promise.all([clearAllMutations(), clearAllPhotos()]);
  } catch {
    // IndexedDB không khả dụng — bỏ qua, không chặn đăng xuất.
  }
}

export function useSessionCleanup(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const currentId = user?.id ?? null;
    const prevId = prevUserIdRef.current;
    prevUserIdRef.current = currentId;

    if (!shouldCleanupSession(prevId, currentId)) return;

    // có user → null (đăng xuất) HOẶC id đổi (đổi người dùng): dọn sạch.
    queryClient.clear();
    void clearServiceWorkerApiCaches();
    void clearOfflineQueues();
  }, [user?.id, queryClient]);
}
